export const ROAS_COVENANT_STATUS = {
  MET: 'met',
  FAILED: 'failed',
  NOT_APPLICABLE: 'not_applicable',
  PENDING: 'pending',
};

const summarizeBasis = (cells, meetsKey) => {
  const values = cells
    .map((cell) => cell[meetsKey])
    .filter((value) => value !== undefined && value !== null);

  if (values.length === 0) {
    return { status: ROAS_COVENANT_STATUS.NOT_APPLICABLE, failedMobs: [] };
  }

  const failedMobs = [];
  for (const cell of cells) {
    const meets = cell[meetsKey];
    if (meets === false && cell.thresholdCovenantMonth != null) {
      failedMobs.push(cell.thresholdCovenantMonth);
    }
  }

  if (failedMobs.length > 0) {
    return {
      status: ROAS_COVENANT_STATUS.FAILED,
      failedMobs: [...new Set(failedMobs)].sort((a, b) => a - b),
    };
  }

  if (values.every((value) => value === true)) {
    return { status: ROAS_COVENANT_STATUS.MET, failedMobs: [] };
  }

  return { status: ROAS_COVENANT_STATUS.PENDING, failedMobs: [] };
};

const collectCovenantCells = (roasCohort, targetPeriods) => {
  const rows = roasCohort?.rows ?? [];
  const cells = [];

  for (const row of rows) {
    for (const cell of row.cells ?? []) {
      if (
        cell?.thresholdCovenantMonth != null &&
        targetPeriods.has(cell.thresholdCovenantMonth)
      ) {
        cells.push(cell);
      }
    }
  }

  return cells;
};

/**
 * Worst-case rollup (Option C): gross/net is Met only if every applicable
 * covenant cell on every cohort row passes.
 */
export function computeRoasTargetCovenantSummary({ roasTargets, roasCohort }) {
  if (!roasTargets?.length) {
    return null;
  }

  const targetPeriods = new Set(
    roasTargets.map((target) => Number(target.period)).filter(Number.isFinite)
  );
  if (targetPeriods.size === 0) {
    return null;
  }

  const covenantCells = collectCovenantCells(roasCohort, targetPeriods);
  if (covenantCells.length === 0) {
    return {
      gross: ROAS_COVENANT_STATUS.PENDING,
      net: ROAS_COVENANT_STATUS.PENDING,
      failedGrossMobs: [],
      failedNetMobs: [],
      evaluatedCellCount: 0,
    };
  }

  const gross = summarizeBasis(covenantCells, 'meetsThresholdGross');
  const net = summarizeBasis(covenantCells, 'meetsThresholdNet');

  return {
    gross: gross.status,
    net: net.status,
    failedGrossMobs: gross.failedMobs,
    failedNetMobs: net.failedMobs,
    evaluatedCellCount: covenantCells.length,
  };
};

export function formatRoasCovenantBasisStatus(status, failedMobs = []) {
  switch (status) {
    case ROAS_COVENANT_STATUS.MET:
      return 'Met at all covenant periods';
    case ROAS_COVENANT_STATUS.FAILED: {
      const mobList = failedMobs.map((mob) => `MOB ${mob}`).join(', ');
      return mobList ? `Not met (${mobList})` : 'Not met';
    }
    case ROAS_COVENANT_STATUS.NOT_APPLICABLE:
      return 'Not applicable';
    case ROAS_COVENANT_STATUS.PENDING:
      return 'Pending — insufficient cohort data';
    default:
      return '—';
  }
}

export function roasCovenantOverallAlertStatus(summary) {
  if (!summary) {
    return 'info';
  }
  const statuses = [summary.gross, summary.net].filter(
    (status) => status !== ROAS_COVENANT_STATUS.NOT_APPLICABLE
  );
  if (statuses.includes(ROAS_COVENANT_STATUS.FAILED)) {
    return 'error';
  }
  if (statuses.includes(ROAS_COVENANT_STATUS.PENDING)) {
    return 'warning';
  }
  if (statuses.length > 0 && statuses.every((status) => status === ROAS_COVENANT_STATUS.MET)) {
    return 'success';
  }
  return 'info';
}

export function roasCovenantBasisTextColor(status) {
  switch (status) {
    case ROAS_COVENANT_STATUS.MET:
      return 'green.600';
    case ROAS_COVENANT_STATUS.FAILED:
      return 'red.600';
    case ROAS_COVENANT_STATUS.PENDING:
      return 'orange.600';
    default:
      return 'gray.600';
  }
}

export function formatRoasTargetCovenantSummaryMessage(summary) {
  if (!summary) {
    return '';
  }
  const grossText = formatRoasCovenantBasisStatus(
    summary.gross,
    summary.failedGrossMobs
  );
  const netText = formatRoasCovenantBasisStatus(summary.net, summary.failedNetMobs);
  return `ROAS Target by Period covenant: Gross — ${grossText}. Net — ${netText}.`;
}
